import TuneIcon from '@mui/icons-material/Tune';
import BusinessIcon from '@mui/icons-material/Business';
import QueueIcon from '@mui/icons-material/FormatListNumbered';
import BadgeIcon from '@mui/icons-material/Badge';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SecurityIcon from '@mui/icons-material/Security';
import BackupIcon from '@mui/icons-material/Backup';

import { temPermissao } from '../../services/auth';
import { funcionalidadeAtiva } from '../../services/configuracao';

// Fonte única das seções da central de Parâmetros: barra de navegação interna,
// migalha, rota (/parametros/<rota>) e guarda de acesso saem todas daqui.
//
// Só metadado — nenhum componente de tela é importado neste arquivo. A barra
// lateral principal (utils/modulos.jsx) consulta `secoesPermitidas()` para
// decidir se mostra o item "Parâmetros", e ela é carregada no bundle inicial:
// importar Empresa e CanalEntrega aqui arrastaria as duas telas junto. O mapa
// de chave → componente vive no Parametros.jsx, que já é lazy.
//
// `categoria` é a categoria de parâmetro chave/valor da API; seção sem
// categoria renderiza um componente próprio (Empresa, Canal de envio,
// Checklist, Backup).
//
// `expandido` diz como o grupo aparece na subnavegação horizontal: aberto, com
// um botão por seção, ou fechado num menu suspenso rotulado pelo grupo. É
// escolha de espaço na barra, não regra — Comunicação tem uma seção só e
// continua fechada porque vai crescer.
export const GRUPOS = [
    {
        id: 'geral',
        titulo: 'Geral',
        expandido: true,
        itens: [
            {
                chave: 'geral', rota: 'geral', titulo: 'Geral', icone: <TuneIcon />,
                descricao: 'Identificação do sistema e origens aceitas.',
                categoria: 'geral', permissao: 'PARAMETROS_VISUALIZAR',
            },
            {
                chave: 'empresa', rota: 'empresa', titulo: 'Empresa', icone: <BusinessIcon />,
                descricao: 'Dados cadastrais desta instalação, usados em cabeçalhos de relatórios e impressões.',
                // Mesma permissão da rota /empresa de antes: mover a tela para
                // dentro de Parâmetros não muda quem entra nela.
                permissao: 'PARAMETROS_VISUALIZAR',
            },
        ],
    },
    {
        id: 'operacao',
        titulo: 'Operação',
        itens: [
            {
                chave: 'fila', rota: 'fila', titulo: 'Fila', icone: <QueueIcon />,
                descricao: 'Comportamento das filas de descarga e carga.',
                categoria: 'fila', permissao: 'PARAMETROS_VISUALIZAR',
            },
            {
                chave: 'locais', rota: 'locais', titulo: 'Locais de atendimento', icone: <QueueIcon />,
                descricao: 'Docas de carga e locais de descarga desta empresa.',
                permissao: 'PARAMETROS_VISUALIZAR',
            },
            {
                chave: 'cadastro', rota: 'cadastros', titulo: 'Cadastros', icone: <BadgeIcon />,
                descricao: 'Como os cadastros de motorista, transportadora e variedade são gravados.',
                categoria: 'cadastro', permissao: 'PARAMETROS_VISUALIZAR',
            },
            {
                chave: 'checklist', rota: 'checklist', titulo: 'Checklist', icone: <FactCheckIcon />,
                descricao: 'Perguntas do checklist de inspeção da fila de carga — texto, tipo de resposta e ordem no formulário.',
                permissao: 'PARAMETROS_VISUALIZAR',
            },
        ],
    },
    {
        id: 'experiencia',
        titulo: 'Experiência',
        expandido: true,
        itens: [
            {
                chave: 'painel', rota: 'painel-voz', titulo: 'Painel e voz', icone: <RecordVoiceOverIcon />,
                descricao: 'Anúncios sonoros do painel de chamadas.',
                categoria: 'painel', permissao: 'PARAMETROS_VISUALIZAR',
            },
        ],
    },
    {
        // Grupo com um item só de propósito: é onde entram notificações,
        // templates e eventos de disparo quando existirem. Nada disso agora.
        id: 'comunicacao',
        titulo: 'Comunicação',
        itens: [
            {
                chave: 'canal-envio', rota: 'canal-envio', titulo: 'Canal de envio', icone: <WhatsAppIcon />,
                descricao: 'Por onde o documento do motorista sai do sistema.',
                // Regra própria, herdada da rota /canal-entrega: permissão
                // diferente da de Parâmetros e funcionalidade própria. Fundir
                // as duas numa permissão só daria acesso a quem não tem hoje.
                permissao: 'CANAL_ENTREGA_VISUALIZAR', funcionalidade: 'NOVO_CARREGAMENTO',
            },
        ],
    },
    {
        id: 'sistema',
        titulo: 'Sistema',
        itens: [
            {
                chave: 'seguranca', rota: 'seguranca', titulo: 'Segurança', icone: <SecurityIcon />,
                descricao: 'Sessão, acesso e proteção dos dados.',
                categoria: 'seguranca', permissao: 'PARAMETROS_VISUALIZAR',
            },
            {
                chave: 'backup', rota: 'backup', titulo: 'Backup', icone: <BackupIcon />,
                descricao: 'Cópia do banco para guardar fora do servidor.',
                permissao: 'PARAMETROS_VISUALIZAR',
            },
        ],
    },
];

// Lista plana de todas as seções, na ordem em que aparecem na navegação.
export const SECOES = GRUPOS.flatMap((grupo) => grupo.itens);

// Permissões que abrem alguma seção da central. A rota /parametros é liberada
// para quem tem qualquer uma delas — e a seção em si é conferida depois, item
// a item.
export const PERMISSOES_PARAMETROS = [...new Set(SECOES.map((s) => s.permissao))];

const acessivel = (secao) =>
    (!secao.funcionalidade || funcionalidadeAtiva(secao.funcionalidade))
    && temPermissao(secao.permissao);

// Grupos com apenas as seções que o perfil pode ver nesta instalação. Grupo sem
// seção some — o título é só organização visual, não tem destino próprio.
export const gruposPermitidos = () => GRUPOS
    .map((grupo) => ({ ...grupo, itens: grupo.itens.filter(acessivel) }))
    .filter((grupo) => grupo.itens.length > 0);

export const secoesPermitidas = () => SECOES.filter(acessivel);

export const secaoPorRota = (rota) => SECOES.find((s) => s.rota === rota) || null;
